import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apolloConfigEntriesOfKind } from "@signalist/shared-types";

import { useNavigate, useParams } from "react-router-dom";

import { faAdd, faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { TextField } from "@mui/material";

import settingsWheel from "@assets/icons/settingsWheel.svg?url";
import trashIcon from "@assets/icons/trashIcon.svg?url";

import { Button } from "@/components/Button/Button";
import { TextArea } from "@/components/TextArea/TextArea";
import {
	useDeleteListColumnMutation,
	useGetListQuery,
	useUpdateListMutation,
} from "@/pages/Private/redux/list/list.api";

import { Icon, LoadingOverlay, Modal } from "@/components";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { ScrapeLevel } from "@/enum/job.enum";

import { CustomColumnTypes, ListSourceType, UpdateType } from "@/enum/list.enum";

import {
	useSaveSearchApolloPeopleMutation,
	useUpdateApolloMutation,
} from "@/pages/Private/redux/apollo/apollo.api";

import { ApolloType } from "@/enum/apollo.enum";
import { ApolloPeopleRequestParams } from "@/types/apollo";

import { classNames } from "@/utils/classNames";

import { StepInfo } from "@/components/StepInfo/StepInfo";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { ListBuilderLayout } from "../../components/ListBuilderLayout";

import { getAvailableSteps, isDetailsNeeded, getDoneSteps } from "../../utils/create-list-steps";
import { useFilters } from "../../utils/useFilters";
import { stripRelativeDateFields } from "../../utils/relativeDateUtils";
import { IFilterWithValues } from "../../components/FilterModal";
import {
	IFilter,
	FrequentlyUsedFiltersConfig,
	AdditionalColumnsFiltersConfig,
} from "../../constants/general-filter";
import {
	AdditionalColumnsJobFiltersConfig,
	AdditionalColumnsNonLinkedinJobFiltersConfigForCreation,
	FrequentlyUsedJobFiltersConfig,
} from "../../constants/jobs-filter";
import { LiCompanyActivityFilter } from "../../constants/li-company-activity-filter";
import { LiPostFilter } from "../../constants/li-post-filter";
import { LiProfileFilter } from "../../constants/li-profile-filter";
import { MapsAdditionalFilter, MapsFilter } from "../../constants/maps-filter";
import { FilterCategory } from "../../enums/filter.enum";
import { ContactsProspectFilters } from "../../../Contacts/constants/contacts-filter";
import { IConfig } from "./interfaces/config-interface";
import { mapApolloConfigToFilter } from "./utils/mapApolloConfigToFilter";
import { buildFilterStepConfig } from "./utils/buildFilterStepConfig";
import { UnreadableFilterRows } from "./components/UnreadableFilterRows";
import { Stepper } from "../ListEditDetails/components/Stepper";
import { EditCustomColumnModalWrapper } from "../../components/EditCustomColumnModalWrapper";
import { CreateCustomColumnModal } from "../../components/CreateCustomColumnModal";
import { ColumnConfig } from "../../schema/columnConfig";
import { List } from "../../schema/list";
import { FeaturesCodes } from "../../../Users/enum/codes";
import { CompanySegmentsSection } from "./components/CompanySegmentsSection";

export const ListEditFilter: FunctionComponent = () => {
	const { t } = useTranslation();
	const { id = 0 } = useParams();

	const navigate = useNavigate();
	const ts = (key: string) => t(`list.${key}`);

	const { data, isFetching } = useGetListQuery(+id);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);
	const isSmartListActive = useIsFeatureActive(FeaturesCodes.SMART_LIST);

	const [expandFrequentlyUsed, setExpandFrequentlyUsed] = useState<boolean>(true);

	const [showNewColumn, setShowNewColumn] = useState<boolean>(false);
	const [editableColumn, setEditableColumn] = useState<ColumnConfig>();
	const [showEditColumn, setShowEditColumn] = useState<boolean>(false);
	const [showDeleteColumn, setShowDeleteColumn] = useState<boolean>(false);
	const [columns, setColumns] = useState<boolean>(false);

	// const { page, limit } = useSearch();

	const isSmartList = data?.sourceType === ListSourceType.SMART_LIST;

	useEffect(() => {
		if (isSmartList && isSmartListActive === false) {
			navigate(`/app/lists/detail/${id}`);
		}
	}, [isSmartList, isSmartListActive, navigate, id]);

	// The filter step is only part of a smart list's flow when it carries a legacy filter /
	// semantic-search query (created from the Contacts module). Catalog-created smart lists filter
	// per-source in the Source step, so the tab is hidden — redirect direct URL access back there.
	useEffect(() => {
		if (
			isSmartList &&
			data &&
			!data.smartList?.filter &&
			!data.smartList?.semanticSearchProspects
		) {
			navigate(`/app/lists/create/source/${id}`);
		}
	}, [isSmartList, data, navigate, id]);

	const [saveSearch, { isLoading }] = useSaveSearchApolloPeopleMutation();
	const [updateSearch, { isLoading: isUpdating }] = useUpdateApolloMutation();
	const [updateList, { isLoading: isUpdatingList }] = useUpdateListMutation();

	const [semanticSearchProspects, setSemanticSearchProspects] = useState<string>("");

	const [filter, setFilter] = useState<IFilterWithValues>();
	// PREC-4622: stored filters nothing can be read from; shown apart and kept until removed.
	const [unreadable, setUnreadable] = useState<Record<string, unknown>>({});

	const [deleteColumn, { isLoading: deleteLoading }] = useDeleteListColumnMutation();

	const { renderFilters, filterValue, isDirty } = useFilters({
		filter,
		sourceType: data?.sourceType,
		filterDefinitions: isSmartList ? ContactsProspectFilters : undefined,
	});

	const [additionalColumns, setAdditionalColumns] = useState<boolean>(false);

	// Load existing Smart List filter data
	useEffect(() => {
		if (isSmartList && data?.smartList) {
			if (data.smartList.filter) {
				try {
					const parsed = JSON.parse(data.smartList.filter);

					setFilter(parsed);
				} catch {
					// ignore invalid JSON
				}
			}

			if (data.smartList.semanticSearchProspects) {
				setSemanticSearchProspects(data.smartList.semanticSearchProspects);
			}
		}
	}, [isSmartList, data]);

	useEffect(() => {
		if (data?.apollos?.length) {
			const apollo = data.apollos.find((apollo) => apollo.type === ApolloType.SOURCE);
			const config = apollo?.config as unknown as IConfig;

			if (!config) {
				return;
			}

			setFilter(mapApolloConfigToFilter(config, filter));
			setUnreadable(apolloConfigEntriesOfKind(config, "unreadable"));
		}
	}, [data]);

	const FrequentlyUsedFilters = useMemo(() => {
		if (isSmartList) {
			return ContactsProspectFilters.filter((f) => f.group === FilterCategory.FREQUENTLY_USED);
		}

		let FilterDataType: IFilter[] = [];

		if (data?.sourceType === ListSourceType.LI_EVENTS) {
			FilterDataType = FrequentlyUsedFiltersConfig;
		}

		if (data?.sourceType === ListSourceType.CSV_ENRICHMENTS) {
			FilterDataType = FrequentlyUsedFiltersConfig;
		}

		if (data?.sourceType === ListSourceType.EMPTY) {
			FilterDataType = FrequentlyUsedFiltersConfig;
		}

		if (data?.sourceType === ListSourceType.MAPS) {
			FilterDataType = MapsFilter;

			if (data?.scrapeLevel === ScrapeLevel.GET_ENRICHMENT) {
				FilterDataType = [...FilterDataType, ...FrequentlyUsedFiltersConfig];
			}
		}

		if (data?.sourceType === ListSourceType.JOBS) {
			FilterDataType = FrequentlyUsedJobFiltersConfig;

			if (
				data.scrapeLevel &&
				[ScrapeLevel.GET_ENRICHMENT, ScrapeLevel.FIND_PERSON_AT_COMPANY].includes(data.scrapeLevel)
			) {
				FilterDataType = [...FilterDataType, ...FrequentlyUsedFiltersConfig];
			}
		}

		if (data?.sourceType === ListSourceType.LI_POST) {
			FilterDataType = [...FrequentlyUsedFiltersConfig, ...LiPostFilter];
		}

		if (data?.sourceType && [ListSourceType.LI_PROFILE].includes(data.sourceType)) {
			FilterDataType = [...FrequentlyUsedFiltersConfig, ...LiProfileFilter];
		}

		if (data?.sourceType === ListSourceType.LI_COMPANY_ACTIVITY) {
			FilterDataType = [...FrequentlyUsedFiltersConfig, ...LiCompanyActivityFilter];
		}

		if (FilterDataType.length === 0) {
			FilterDataType = FrequentlyUsedFiltersConfig;
		}

		FilterDataType = FilterDataType.filter(
			(filter) =>
				filter.name !== "title" && filter.name !== "country" && filter.name !== "seniority"
		);

		return FilterDataType;
	}, [data, isSmartList]);

	const additionalColumnsFilters = useMemo(() => {
		if (isSmartList) {
			return ContactsProspectFilters.filter((f) => f.group !== FilterCategory.FREQUENTLY_USED);
		}

		let FilterDataType: IFilter[] = [];

		if (data && FrequentlyUsedFilters?.length) {
			if (!FrequentlyUsedFilters) {
				return FilterDataType;
			}

			if (data?.sourceType === ListSourceType.CSV_ENRICHMENTS) {
				FilterDataType = AdditionalColumnsFiltersConfig;
			}

			if (data?.sourceType === ListSourceType.LI_EVENTS) {
				FilterDataType = AdditionalColumnsFiltersConfig;
			}

			if (data?.sourceType === ListSourceType.MAPS) {
				FilterDataType = MapsAdditionalFilter;

				if (data?.scrapeLevel === ScrapeLevel.GET_ENRICHMENT) {
					FilterDataType = [
						...FilterDataType,
						...AdditionalColumnsFiltersConfig.filter(
							(filter) =>
								!FrequentlyUsedFilters.find((f) => f.name === filter.name) &&
								!FilterDataType.find((f) => f.name === filter.name)
						),
					];
				}
			}

			if (data?.sourceType === ListSourceType.JOBS) {
				FilterDataType = AdditionalColumnsJobFiltersConfig;
				if (data?.scrapeLevel === ScrapeLevel.GET_ENRICHMENT) {
					FilterDataType = [
						...FilterDataType,
						...AdditionalColumnsFiltersConfig.filter(
							(filter) =>
								!FrequentlyUsedFilters.find((f) => f.name === filter.name) &&
								!FilterDataType.find((f) => f.name === filter.name)
						),
					];
				} else {
					FilterDataType = AdditionalColumnsNonLinkedinJobFiltersConfigForCreation;
				}
			}

			if (data?.sourceType === ListSourceType.LI_POST) {
				FilterDataType = AdditionalColumnsFiltersConfig;
			}

			if (data?.sourceType && [ListSourceType.LI_PROFILE].includes(data.sourceType)) {
				FilterDataType = AdditionalColumnsFiltersConfig;
			}

			if (data?.sourceType === ListSourceType.LI_COMPANY_ACTIVITY) {
				FilterDataType = AdditionalColumnsFiltersConfig;
			}

			if (FilterDataType.length === 0) {
				FilterDataType = AdditionalColumnsFiltersConfig.filter(
					(filter) => !FrequentlyUsedFilters.find((f) => f.name === filter.name)
				);
			}

			FilterDataType = FilterDataType.filter(
				(filter) => !FrequentlyUsedFilters.find((f) => f.name === filter.name)
			);
		}

		FilterDataType = FilterDataType.filter(
			(filter) =>
				filter.name !== "companyName" &&
				filter.name !== "language" &&
				filter.name !== "previousEmployer" &&
				filter.name !== "companyLinkedinUrl" &&
				filter.name !== "about" &&
				filter.name !== "state" &&
				filter.name !== "city"
		);

		return FilterDataType;
	}, [data, FrequentlyUsedFilters]);

	const handleDeleteColumn = async () => {
		await deleteColumn({ id: editableColumn?.id }).unwrap();
		setEditableColumn(undefined);
	};

	const submitSmartList = async () => {
		// Build the filter JSON from filterValue (same format as Contacts module)
		const cleanFilter: IFilterWithValues = {};

		if (filterValue) {
			for (const key of Object.keys(filterValue)) {
				const entry = filterValue[key];

				if (!entry) {
					continue;
				}

				// Skip empty dropdown/dropdownCustom arrays
				if (
					(entry.type === "dropdown" ||
						entry.type === "dropdownExclude" ||
						entry.type === "dropdownCustom" ||
						entry.type === "dropdownCustomExclude") &&
					Array.isArray(entry.value) &&
					(entry.value as { id: string; title: string }[]).length === 0
				) {
					continue;
				}

				// Skip empty text filters
				if (
					[
						"textContains",
						"textDoesNotContain",
						"textStartsWith",
						"textEndsWith",
						"textIsExactly",
						"greaterEqual",
						"lessEqual",
					].includes(entry.type) &&
					!entry.value
				) {
					continue;
				}

				// Skip incomplete between filters
				if (
					entry.type === "between" &&
					(!(entry.value as { till?: number; from?: number })?.till ||
						!(entry.value as { till?: number; from?: number })?.from)
				) {
					continue;
				}

				cleanFilter[key] = stripRelativeDateFields(entry);
			}
		}

		const filterString = Object.keys(cleanFilter).length > 0 ? JSON.stringify(cleanFilter) : null;

		await updateList({
			id: +id,
			updateType: UpdateType.SETTINGS,
			smartList: {
				...(data?.smartList || {}),
				filter: filterString,
				semanticSearchProspects: semanticSearchProspects || null,
			},
		}).unwrap();

		navigate(
			isSequencerActive ? `/app/lists/create/copywriting/${id}` : `/app/lists/create/settings/${id}`
		);
	};

	const submit = async () => {
		if (isSmartList) {
			return submitSmartList();
		}

		const config = buildFilterStepConfig(
			filterValue,
			data?.apollos?.[0]?.config ?? {},
			unreadable
		) as ApolloPeopleRequestParams;

		if (data?.apollos && data?.apollos.length > 0) {
			updateSearch({
				id: data.apollos.find((apollo) => apollo.type === ApolloType.SOURCE)?.id || 0,
				name: data?.name || "",
				type: ApolloType.SOURCE,
				config,
				listId: +id,
			});
		} else {
			saveSearch({
				name: data?.name || "",
				type: ApolloType.SOURCE,
				config,
				leadsPerDay: 0,
				amount: 0,
				listId: +id,
			});
		}

		if (isDetailsNeeded(data as List)) {
			navigate(`/app/lists/create/details/${id}`);
		} else if (isSequencerActive) {
			navigate(`/app/lists/create/copywriting/${id}`);
		} else {
			navigate(`/app/lists/create/settings/${id}`);
		}
	};

	const handleEdit = (column: ColumnConfig) => {
		setEditableColumn(column);
		setShowEditColumn(true);
	};
	const handleDelete = (column: ColumnConfig) => {
		setEditableColumn(column);
		setShowDeleteColumn(true);
	};

	return isFetching || !data ? (
		<LoadingOverlay />
	) : (
		<ListBuilderLayout bodyClassName="w-full p-4 pb-32" sourceTypeLabel={ts(data.sourceType)}>
			<Stepper
				availableSteps={getAvailableSteps(isDetailsNeeded(data), data, isSequencerActive)}
				currentStep="filter"
				doneSteps={getDoneSteps(data)}
				isDirty={isDirty}
				listId={data.id}
				sourceType={data.sourceType}
			/>
			{isSmartList ? (
				<StepInfo
					description={<p>{t("contacts.smartList.filterDescription")}</p>}
					title={t("contacts.smartList.filterTitle")}
				/>
			) : (
				<StepInfo
					description={
						<>
							<p>
								All leads will be checked against the qualification criteria below. If the criteria
								are fulfilled, the lead will be put to “Qualified”. If a criterion is not met or not
								found, the company will be filtered out and we will not try to find a person there.
								It is advisable to be not too strict as you can filter within the lists as well.
							</p>
							<p>Credits will be consumed regardless of whether leads pass the criteria or not.</p>
						</>
					}
					title="Define qualification criteria"
					videoId="3SRCpP7T8v0"
				/>
			)}

			{isSmartList && (
				<div className="border p-6 mb-8 w-full items-start rounded-xl border-1 border-border-light flex flex-col text-left gap-4 bg-white">
					<div className="text-smd text-text font-bold">
						{t("contacts.smartList.semanticSearchTitle")}
					</div>
					<div className="text-ssm text-text-meta">
						{t("contacts.smartList.semanticSearchDescription")}
					</div>
					<TextArea
						handleChange={(e) => setSemanticSearchProspects(e.target.value)}
						label={t("contacts.smartList.semanticSearchTitle")}
						name="semanticSearchProspects"
						placeholder={t("contacts.smartList.semanticSearchPlaceholder")}
						showError={false}
						value={semanticSearchProspects}
					/>
				</div>
			)}

			{!isSmartList && (
				<UnreadableFilterRows
					entries={unreadable}
					onRemove={(key) => setUnreadable(({ [key]: _removed, ...rest }) => rest)}
				/>
			)}

			{!isSmartList && <CompanySegmentsSection listId={+id} />}

			<div
				className={classNames(
					"border p-6 mb-8 w-full items-start rounded-xl border-1 border-border-light",
					"flex flex-col text-left gap-6",
					expandFrequentlyUsed ? "bg-background-light" : "bg-white"
				)}
			>
				<button
					className={classNames(
						"w-full items-center group cursor-pointer flex flex-row text-left gap-6"
					)}
					onClick={() => {
						setExpandFrequentlyUsed(!expandFrequentlyUsed);
					}}
				>
					<div className="grow">
						<div className="text-smd text-text font-bold">Frequently used attributes</div>
						{/* <div className="text-ssm text-text-meta">Attributes that are frequently used</div> */}
					</div>
					<div
						className={classNames(
							"flex min-w-[36px] min-h-[36px] !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
							expandFrequentlyUsed
								? "border-border-light bg-white"
								: "border-transparent bg-background-light"
						)}
					>
						<Icon
							className="text-text-meta group-hover:text-white w-[12px] h-auto"
							icon={expandFrequentlyUsed ? faChevronUp : faChevronDown}
						/>
					</div>
				</button>
				{expandFrequentlyUsed && (
					<div className="flex flex-row w-full">{renderFilters(FrequentlyUsedFilters)}</div>
				)}
			</div>
			<div
				className={classNames(
					"border p-6 mb-8 w-full items-start rounded-xl border-1 border-border-light",
					"flex flex-col text-left gap-6",
					additionalColumns ? "bg-background-light" : "bg-white"
				)}
			>
				<button
					className={classNames(
						"w-full items-center group cursor-pointer flex flex-row text-left gap-6"
					)}
					onClick={() => {
						setAdditionalColumns(!additionalColumns);
					}}
				>
					<div className="grow">
						<div className="text-smd text-text font-bold">Additional attributes</div>
						<div className="text-ssm text-text-meta">
							Filter by even more attributes like Founded Year and Company Branch Locations
						</div>
					</div>
					<div
						className={classNames(
							"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
							additionalColumns
								? "border-border-light bg-white"
								: "border-transparent bg-background-light"
						)}
					>
						<Icon
							className="text-text-meta group-hover:text-white w-[12px] h-auto"
							icon={additionalColumns ? faChevronUp : faChevronDown}
						/>
					</div>
				</button>

				{additionalColumns && (
					<div className="flex flex-row w-full">{renderFilters(additionalColumnsFilters)}</div>
				)}
			</div>
			<div
				className={classNames(
					"hidden border p-6 mb-8 w-full items-start rounded-md border-1 border-border-light",
					"border-inactive-item",
					"flex flex-col text-left gap-6",
					expandFrequentlyUsed ? "bg-background-light" : "bg-white"
				)}
			>
				<button
					className={classNames(
						"w-full items-center group cursor-pointer flex flex-row text-left gap-6"
					)}
					onClick={() => {
						setColumns(!columns);
					}}
				>
					<div className="grow">
						<div className="text-smd text-text font-bold">Custom Columns</div>
						<div className="text-ssm text-text-meta">Custom columns</div>
					</div>
					<div
						className={classNames(
							"flex !w-[36px] !h-[36px]  group-hover:bg-blue-action rounded-md items-center justify-center border",
							columns ? "border-border-light bg-white" : "border-transparent bg-background-light"
						)}
					>
						<Icon
							className="text-text-meta group-hover:text-white w-[12px] h-auto"
							icon={columns ? faChevronUp : faChevronDown}
						/>
					</div>
				</button>

				{columns && (
					<>
						<div className="flex flex-col">
							{data?.columns
								?.filter((c) => c.type !== CustomColumnTypes.AI_PROMPT)
								?.map((column) => (
									<div
										key={column.id}
										className="flex flex-row items-center p-2 space-x-2 bg-white border border-gray-200 rounded-xl mb-4 px-4 py-3 w-[370px]"
									>
										<div className="flex grow">{column.name}</div>
										<div className="flex flex-row items-center ml-auto">
											<button className="mr-2" onClick={() => handleEdit(column)}>
												<img alt="settingsWheel" className="cursor-pointer" src={settingsWheel} />
											</button>
											<button onClick={() => handleDelete(column)}>
												<img alt="delete" className="cursor-pointer" src={trashIcon} />
											</button>
										</div>
									</div>
								))}
						</div>
						<div className="flex flex-row gap-4 w-full mb-4">
							<div className="w-auto">
								<Button
									color={ButtonColor.BRAND}
									image={<Icon className="mr-2" icon={faAdd} />}
									size={ButtonSize.ML}
									title={t("inbox.addColumn")}
									onClick={() => setShowNewColumn(true)}
								/>
							</div>
						</div>
					</>
				)}
			</div>

			<div className="absolute mt-14 h-16 flex flex-row justify-between items-center w-auto border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-[999] bg-white w-full">
				<div></div>
				<div className="flex flex-row self-left items-center gap-x-4">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						title={t("basics.previous")}
						onClick={() => {
							// The filter step always follows the source step (incl. smart lists).
							navigate(`/app/lists/create/source/${id}`);
						}}
					/>
					<Button
						isLoading={isLoading || isUpdating || isUpdatingList}
						title={t("basics.next")}
						onClick={submit}
					/>
				</div>
			</div>
			{showNewColumn && id && (
				<CreateCustomColumnModal
					customColumnTypes={[
						CustomColumnTypes.TEXT,
						CustomColumnTypes.NUMBER,
						CustomColumnTypes.BOOLEAN,
						CustomColumnTypes.LINK,
						CustomColumnTypes.RANDOM_VALUE,
					]}
					listId={+id}
					listType={data.sourceType}
					open={showNewColumn}
					setOpen={setShowNewColumn}
				/>
			)}
			{showEditColumn && id && editableColumn?.id && (
				<EditCustomColumnModalWrapper
					columnId={editableColumn?.id}
					listId={+id}
					open={showEditColumn}
					setOpen={setShowEditColumn}
				/>
			)}
			{showDeleteColumn && id && editableColumn?.id && (
				<Modal
					handleClose={() => setShowDeleteColumn(false)}
					handleSave={handleDeleteColumn}
					isLoading={deleteLoading}
					isOpened={showDeleteColumn}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={t("basics.delete")}
					title={ts("delete.title")}
				>
					<div className="text-sm text-gray-700 mb-4">{ts("delete.confirmation")}</div>
					<TextField
						fullWidth
						defaultValue={editableColumn.name}
						inputProps={{ readOnly: true }}
						label={ts("name")}
					/>
				</Modal>
			)}
		</ListBuilderLayout>
	);
};
